import { useState } from 'react';
import type { Player } from '../game/identity';
import {
  isPlausibleCode,
  isPlausibleEmail,
  isPlausibleName,
  requestCode,
  verifyCode,
} from '../game/identity';
import { BrandHeader } from './BrandHeader';
import { RTNav } from './RTNav';

interface Props {
  onSignedIn: (player: Player) => void;
  onShowPrivacy: () => void;
  /**
   * The case the player was trying to open. Sign-in is no longer the front
   * door — it interrupts someone on their way somewhere, and saying where
   * turns an unexplained email form into an obvious last step.
   */
  pendingTitle?: string | undefined;
  /** Let them back out. An interruption with no exit is a trap. */
  onCancel?: (() => void) | undefined;
}

type Step = 'details' | 'code';

/**
 * Two-step sign-in: details, then a code sent to the address.
 *
 * The code is what makes the email a real identifier rather than a claim, which
 * in turn is what lets the app offer "download my data" without handing someone
 * else's records to whoever guessed their address.
 *
 * The marketing opt-in stays on the first step, separate from the submit button
 * and unticked. It is captured with the request and only committed once the
 * address is proven, so an unverified address never lands in a consent record.
 */
export function SignIn({ onSignedIn, onShowPrivacy, pendingTitle, onCancel }: Props) {
  const [step, setStep] = useState<Step>('details');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [marketing, setMarketing] = useState(false);
  const [code, setCode] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [expiresIn, setExpiresIn] = useState(10);

  const nameOk = isPlausibleName(name);
  const emailOk = isPlausibleEmail(email);
  const codeOk = isPlausibleCode(code);

  const sendCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    setMessage(null);
    if (!nameOk || !emailOk) return;

    setBusy(true);
    const outcome = await requestCode({ email, name, marketingConsent: marketing });
    setBusy(false);

    if (outcome.status === 'sent') {
      setExpiresIn(outcome.expiresInMinutes);
      setTouched(false);
      setStep('code');
      return;
    }
    if (outcome.status === 'rate-limited') {
      setMessage(
        'Too many codes requested for that address. Please wait a few minutes and try again.',
      );
    } else if (outcome.status === 'send-failed') {
      setMessage(
        'We could not deliver the email. This is a problem on our side, not with your address — please let us know at info@releaseteam.com.',
      );
    } else {
      setMessage('We could not send the code just now. Please try again in a moment.');
    }
  };

  const submitCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    setMessage(null);
    if (!codeOk) return;

    setBusy(true);
    const outcome = await verifyCode(email, code);
    setBusy(false);

    switch (outcome.status) {
      case 'verified':
        onSignedIn(outcome.player);
        return;
      case 'wrong-code':
        setMessage(
          outcome.remaining && outcome.remaining > 0
            ? `That code is not right. ${outcome.remaining} ${
                outcome.remaining === 1 ? 'attempt' : 'attempts'
              } left.`
            : 'That code is not right.',
        );
        return;
      case 'expired':
        setMessage('That code has expired. Ask for a new one.');
        return;
      case 'locked':
        setMessage('Too many wrong attempts. Ask for a new code.');
        return;
      default:
        setMessage('Something went wrong verifying that code. Please try again.');
    }
  };

  const startOver = () => {
    setStep('details');
    setCode('');
    setMessage(null);
    setTouched(false);
  };

  return (
    <div className="panel">
      <RTNav />
      <div className="panel__inner panel__inner--narrow">
        <BrandHeader />
        {pendingTitle && <p className="case-eyebrow">Before you start</p>}
        <h1>{pendingTitle ?? 'DevTool Detective'}</h1>

        {step === 'details' ? (
          <>
            <p className="lede">
              {pendingTitle
                ? 'Scores are kept against your email, so we need it before this case starts. We will send a code to confirm the address is yours.'
                : 'Find the misconfigurations hiding in a software delivery tool. We will email you a code to confirm your address, then keep score.'}
            </p>

            <form className="form" onSubmit={sendCode} noValidate>
              <label className="field">
                <span className="field__label">Full name</span>
                <input
                  className="field__input"
                  type="text"
                  name="name"
                  value={name}
                  autoComplete="name"
                  autoCapitalize="words"
                  enterKeyHint="next"
                  placeholder="Ada Lovelace"
                  aria-invalid={touched && !nameOk}
                  onChange={(e) => setName(e.target.value)}
                />
                {touched && !nameOk && (
                  <span className="field__error">Please enter your full name.</span>
                )}
              </label>

              <label className="field">
                <span className="field__label">Email address</span>
                <input
                  className="field__input"
                  type="email"
                  name="email"
                  value={email}
                  autoComplete="email"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  inputMode="email"
                  enterKeyHint="go"
                  placeholder="ada@example.com"
                  aria-invalid={touched && !emailOk}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {touched && !emailOk && (
                  <span className="field__error">
                    That does not look like an email address.
                  </span>
                )}
              </label>

              <p className="collection-notice">
                Your email identifies your scores across levels, and we send one
                code to confirm it is yours. We keep your name, email and scores
                for <b>12 months</b> from your last play, then delete them
                automatically. No analytics, no advertising, no third-party
                tracking, and we never sell or share your information.{' '}
                <button type="button" className="linkish" onClick={onShowPrivacy}>
                  Read the full privacy notice
                </button>
                .
              </p>

              <label className="consent">
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                />
                <span>
                  Email me occasionally about ReleaseTEAM services.{' '}
                  <b>Entirely optional</b> — the game works the same either way,
                  and you can unsubscribe at any time.
                </span>
              </label>

              {message && (
                <p className="form__message" role="alert">
                  {message}
                </p>
              )}

              <button type="submit" className="btn btn--wide" disabled={busy}>
                {busy ? 'Sending your code…' : 'Email me a code'}
              </button>

              {onCancel && (
                <button
                  type="button"
                  className="btn btn--ghost btn--wide"
                  onClick={onCancel}
                >
                  Not now
                </button>
              )}
            </form>
          </>
        ) : (
          <>
            <p className="lede">
              We sent a six-digit code to <strong>{email}</strong>. It expires in{' '}
              {expiresIn} minutes.
            </p>

            <form className="form" onSubmit={submitCode} noValidate>
              <label className="field">
                <span className="field__label">Six-digit code</span>
                <input
                  className="field__input field__input--code"
                  type="text"
                  name="one-time-code"
                  value={code}
                  // Lets phones offer the code straight from the notification.
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  enterKeyHint="go"
                  placeholder="123456"
                  autoFocus
                  aria-invalid={touched && !codeOk}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
                {touched && !codeOk && (
                  <span className="field__error">Enter the six digits from the email.</span>
                )}
              </label>

              {message && (
                <p className="form__message" role="alert">
                  {message}
                </p>
              )}

              <button type="submit" className="btn btn--wide" disabled={busy}>
                {busy ? 'Checking…' : 'Verify and play'}
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--wide"
                style={{ marginTop: 10 }}
                onClick={startOver}
                disabled={busy}
              >
                Use a different address
              </button>

              {onCancel && (
                <button
                  type="button"
                  className="btn btn--ghost btn--wide"
                  onClick={onCancel}
                >
                  Not now
                </button>
              )}
            </form>

            <p className="fineprint">
              No code? Check spam, and make sure the address above is right. Codes
              can be requested a few times before you need to wait.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
